import type { Interceptor } from '@connectrpc/connect';
import { toJson } from '@bufbuild/protobuf';
import type { DescMessage } from '@bufbuild/protobuf';
import { EXTERNALIZATION_THRESHOLD_BYTES } from '../contract';
import {
  CORRELATION_ID_HEADER,
  getRecordingSink,
  type RecordedGrpcCall,
} from './recordingSink';

const encoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

function utf8ByteLength(value: string): number {
  if (encoder) {
    return encoder.encode(value).length;
  }
  // Node/jest fallback when TextEncoder is unavailable.
  return Buffer.byteLength(value, 'utf-8');
}

/** Generate a per-call correlation id (uuid when available, otherwise a unique token). */
export function newCorrelationId(): string {
  const maybeCrypto = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (maybeCrypto?.randomUUID) {
    return maybeCrypto.randomUUID();
  }
  return `cid-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

function perfNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function pad3(value: number): string {
  return String(value).padStart(3, '0');
}

type ResolvedPayload = { inline?: unknown; ref?: string };

/**
 * Inline a payload when it is at/below the contract threshold, otherwise move it
 * to `grpc/step-NNN-{request|response}.json` and return a relative ref.
 */
function resolvePayload(
  stepIndex: number,
  kind: 'request' | 'response',
  json: string,
  artifacts: Record<string, string>,
): ResolvedPayload {
  if (utf8ByteLength(json) > EXTERNALIZATION_THRESHOLD_BYTES) {
    const ref = `grpc/step-${pad3(stepIndex)}-${kind}.json`;
    artifacts[ref] = json;
    return { ref };
  }
  return { inline: JSON.parse(json) };
}

function toJsonString(schema: DescMessage, message: unknown): string {
  try {
    return JSON.stringify(toJson(schema, message as never));
  } catch {
    return JSON.stringify({ _unserializable: true });
  }
}

type EntryParts = {
  stepIndex: number;
  service: string;
  methodName: string;
  correlationId: string;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  status: string;
  error?: string;
  request: ResolvedPayload;
  response?: ResolvedPayload;
};

function buildEntry(parts: EntryParts): RecordedGrpcCall {
  const call: RecordedGrpcCall = {
    index: parts.stepIndex,
    service: parts.service,
    method: `${parts.service}/${parts.methodName}`,
    correlationId: parts.correlationId,
    startedAt: parts.startedAt,
    endedAt: parts.endedAt,
    durationMs: parts.durationMs,
    status: parts.status,
  };
  if (parts.error !== undefined) {
    call.error = parts.error;
  }
  if (parts.request.ref !== undefined) {
    call.requestRef = parts.request.ref;
  } else {
    call.request = parts.request.inline;
  }
  if (parts.response) {
    if (parts.response.ref !== undefined) {
      call.responseRef = parts.response.ref;
    } else {
      call.response = parts.response.inline;
    }
  }
  return call;
}

/**
 * connect-es interceptor that captures every unary web->plugin gRPC call while a
 * recording session is active. It is a zero-overhead pass-through when not
 * recording or for streaming calls. Add it to the transport `interceptors[]`.
 */
export function createGrpcRecorderInterceptor(): Interceptor {
  return (next) => async (req) => {
    const sink = getRecordingSink();
    if (!sink.isRecording() || req.stream) {
      return next(req);
    }

    const correlationId = newCorrelationId();
    if (!req.header.has(CORRELATION_ID_HEADER)) {
      req.header.set(CORRELATION_ID_HEADER, correlationId);
    }

    const service = req.service.typeName;
    const methodName = req.method.name;
    const stepIndex = sink.reserveStepIndex();
    const startedAt = nowIso();
    const startPerf = perfNow();
    const requestJson = toJsonString(req.method.input, req.message);

    try {
      const res = await next(req);
      const durationMs = round1(perfNow() - startPerf);
      const artifacts: Record<string, string> = {};
      const request = resolvePayload(stepIndex, 'request', requestJson, artifacts);
      const responseJson = toJsonString(req.method.output, res.message);
      const response = resolvePayload(stepIndex, 'response', responseJson, artifacts);
      sink.pushGrpcCall(
        buildEntry({
          stepIndex,
          service,
          methodName,
          correlationId,
          startedAt,
          endedAt: nowIso(),
          durationMs,
          status: 'OK',
          request,
          response,
        }),
        artifacts,
      );
      return res;
    } catch (err) {
      const durationMs = round1(perfNow() - startPerf);
      const artifacts: Record<string, string> = {};
      const request = resolvePayload(stepIndex, 'request', requestJson, artifacts);
      sink.pushGrpcCall(
        buildEntry({
          stepIndex,
          service,
          methodName,
          correlationId,
          startedAt,
          endedAt: nowIso(),
          durationMs,
          status: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
          request,
        }),
        artifacts,
      );
      throw err;
    }
  };
}

/** Singleton interceptor wired into the transport. */
export const grpcRecorderInterceptor = createGrpcRecorderInterceptor();

/**
 * Capture a unary call that went through the raw-fetch WebView2 path
 * (`unaryViaDirectGrpcWeb`), which bypasses connect-es interceptors. No-op when
 * not recording. Caller passes pre-serialized JSON payloads so this module stays
 * free of any transport details.
 */
export function recordRawUnaryCall(params: {
  service: string;
  methodName: string;
  correlationId: string;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  status: string;
  error?: string;
  requestJson: string;
  responseJson?: string;
}): void {
  const sink = getRecordingSink();
  if (!sink.isRecording()) {
    return;
  }

  const stepIndex = sink.reserveStepIndex();
  const artifacts: Record<string, string> = {};
  const request = resolvePayload(stepIndex, 'request', params.requestJson, artifacts);
  const response =
    params.responseJson !== undefined
      ? resolvePayload(stepIndex, 'response', params.responseJson, artifacts)
      : undefined;

  sink.pushGrpcCall(
    buildEntry({
      stepIndex,
      service: params.service,
      methodName: params.methodName,
      correlationId: params.correlationId,
      startedAt: params.startedAt,
      endedAt: params.endedAt,
      durationMs: params.durationMs,
      status: params.status,
      error: params.error,
      request,
      response,
    }),
    artifacts,
  );
}
